import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Button, MenuItem, Paper, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

import { mapGoverningBodyOption } from "../../components/FormFields/GoverningBodiesMultiSelect";
import { useRuleStyles } from "../Rules/styles";
import { STATUS_OPTIONS } from "./teamListQuery";

const PINNED_COUNTRY = "US";

const sameText = (a, b) =>
  String(a || "")
    .trim()
    .toLowerCase() ===
  String(b || "")
    .trim()
    .toLowerCase();

/**
 * Filter bar of the Teams list. Selects apply at once; the two text boxes are drafts the page
 * commits after a pause. Division is picked from the values teams actually use, so the match is
 * exact ("II" never brings back "III").
 */
const TeamFilters = ({
  state,
  patch,
  searchDraft,
  onSearchDraft,
  cityDraft,
  onCityDraft,
  governingBodies,
  countries,
  states,
  statesLoading,
  divisions,
  teamTypes,
  canClear,
  onClear,
}) => {
  const classes = useRuleStyles();

  // Same "ABBR Name" options as the governing body picker on the team form; typing matches
  // either part.
  const bodyOptions = useMemo(
    () =>
      (governingBodies || [])
        .map(mapGoverningBodyOption)
        .filter(Boolean)
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        ),
    [governingBodies],
  );

  const bodyValue = useMemo(() => {
    if (!state.governingBodyId) return null;
    const id = Number(state.governingBodyId);
    // A linked id stays shown before the list arrives.
    return bodyOptions.find((o) => o.id === id) || { id, label: `#${id}` };
  }, [bodyOptions, state.governingBodyId]);

  // Only countries teams are in (from filter-options); United States first.
  const sortedCountries = useMemo(() => {
    const list = [...(countries || [])];
    const us = list.findIndex((c) => c.code === PINNED_COUNTRY);
    if (us > 0) list.unshift(list.splice(us, 1)[0]);
    return list;
  }, [countries]);

  const divisionValue = useMemo(() => {
    if (!state.divisionId) return null;
    return (
      (divisions || []).find((d) => sameText(d.value, state.divisionId)) || {
        value: state.divisionId,
        count: null,
      }
    );
  }, [divisions, state.divisionId]);

  const stateHelper = !state.country
    ? "Pick a country first"
    : !statesLoading && !(states || []).length
    ? "No states listed for this country"
    : " ";

  return (
    <Paper className={classes.filters} component="section" aria-label="Filters">
      <TextField
        id="team-filter-search"
        label="Search"
        placeholder="Name or abbreviation"
        variant="outlined"
        size="small"
        className={classes.filterFieldWide}
        value={searchDraft}
        onChange={(e) => onSearchDraft(e.target.value)}
      />
      <TextField
        id="team-filter-status"
        select
        label="Status"
        variant="outlined"
        size="small"
        className={classes.filterFieldNarrow}
        value={state.status}
        onChange={(e) => patch({ status: e.target.value })}
      >
        {STATUS_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        id="team-filter-governing-body"
        className={classes.filterFieldGb}
        options={bodyOptions}
        value={bodyValue}
        onChange={(_, option) =>
          patch({ governingBodyId: option ? String(option.id) : "" })
        }
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Governing body"
            placeholder="All"
            variant="outlined"
            size="small"
          />
        )}
      />
      <TextField
        id="team-filter-team-type"
        select
        label="Team type"
        variant="outlined"
        size="small"
        className={classes.filterField}
        value={state.teamType}
        onChange={(e) => patch({ teamType: e.target.value })}
      >
        <MenuItem value="">All</MenuItem>
        {state.teamType &&
          !(teamTypes || []).some((t) => t.code === state.teamType) && (
            <MenuItem value={state.teamType}>{state.teamType}</MenuItem>
          )}
        {(teamTypes || []).map((t) => (
          <MenuItem key={t.code} value={t.code}>
            {t.code} - {t.label}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        id="team-filter-division"
        className={classes.filterFieldNarrow}
        options={divisions || []}
        value={divisionValue}
        onChange={(_, option) =>
          patch({ divisionId: option ? option.value : "" })
        }
        getOptionLabel={(option) => option.value}
        getOptionSelected={(option, value) =>
          sameText(option.value, value.value)
        }
        renderOption={(option) =>
          option.count == null
            ? option.value
            : `${option.value} (${option.count})`
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Division"
            variant="outlined"
            size="small"
          />
        )}
      />
      <TextField
        id="team-filter-country"
        select
        label="Country"
        variant="outlined"
        size="small"
        className={classes.filterField}
        value={state.country}
        onChange={(e) => patch({ country: e.target.value, state: "" })}
      >
        <MenuItem value="">All</MenuItem>
        {state.country &&
          !sortedCountries.some((c) => c.code === state.country) && (
            <MenuItem value={state.country}>{state.country}</MenuItem>
          )}
        {sortedCountries.map((c) => (
          <MenuItem key={c.code} value={c.code}>
            {c.name} ({c.count})
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="team-filter-state"
        select
        label="State"
        variant="outlined"
        size="small"
        className={classes.filterFieldNarrow}
        value={state.state}
        disabled={!state.country || (!statesLoading && !(states || []).length)}
        helperText={stateHelper}
        onChange={(e) => patch({ state: e.target.value })}
      >
        <MenuItem value="">All</MenuItem>
        {state.state &&
          !(states || []).some((s) => s.abbreviation === state.state) && (
            <MenuItem value={state.state}>{state.state}</MenuItem>
          )}
        {(states || []).map((s) => (
          <MenuItem key={s.abbreviation} value={s.abbreviation}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="team-filter-city"
        label="City"
        variant="outlined"
        size="small"
        className={classes.filterFieldNarrow}
        value={cityDraft}
        onChange={(e) => onCityDraft(e.target.value)}
      />
      <Button
        variant="text"
        color="primary"
        className={classes.touch}
        disabled={!canClear}
        onClick={onClear}
      >
        Clear filters
      </Button>
    </Paper>
  );
};

TeamFilters.propTypes = {
  state: PropTypes.object.isRequired,
  patch: PropTypes.func.isRequired,
  searchDraft: PropTypes.string.isRequired,
  onSearchDraft: PropTypes.func.isRequired,
  cityDraft: PropTypes.string.isRequired,
  onCityDraft: PropTypes.func.isRequired,
  governingBodies: PropTypes.array,
  countries: PropTypes.array,
  states: PropTypes.array,
  statesLoading: PropTypes.bool,
  divisions: PropTypes.array,
  teamTypes: PropTypes.array,
  canClear: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
};

export default TeamFilters;
